// GtLexaRoom: a live coaching session with Lexa, at /lexa/:token.
//
// The link is the key, as with Nathan's interview room, so a staff test run
// works without a candidate account. Steps: a short reminder of how the
// session works, a camera and microphone check, the live call, then done.
// A dropped call or a pause carries on from the same link.
//
// The call runs on LiveKit. Lexa's voice drives her orb; both sides are
// transcribed live. The browser records the call (the candidate's camera and
// both voices) and uploads it in pieces as it goes, so the recording is saved
// on their profile for them to watch again.
//
// This is coaching, not an assessment: unlike Nathan's room there is no rule
// about staying in the window and no full screen.
import { useState, useEffect, useRef, useCallback } from 'react';
import { Room, RoomEvent, Track, createLocalTracks, VideoPresets } from 'livekit-client';
import API_BASE from '../../config';

const TOPIC = 'lk.transcription';
const PIECE_MS = 8000;

function clock(s) {
  const m = Math.floor(s / 60); const r = s % 60;
  return (m < 10 ? '0' : '') + m + ':' + (r < 10 ? '0' : '') + r;
}
async function call(token, path, body) {
  const res = await fetch(API_BASE + '/api/public/lexa/' + token + (path || ''), body === undefined ? {} : {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
  });
  const j = await res.json().catch(() => ({}));
  if (!res.ok) { const e = new Error(j.message || 'Something went wrong.'); e.status = res.status; e.code = j.error; throw e; }
  return j;
}
function pickMime() {
  if (typeof window === 'undefined' || !window.MediaRecorder) return null;
  const options = ['video/webm;codecs=vp8,opus', 'video/webm;codecs=vp9,opus', 'video/webm', 'video/mp4;codecs=avc1,mp4a', 'video/mp4'];
  return options.find((m) => { try { return window.MediaRecorder.isTypeSupported(m); } catch (e) { return false; } }) || null;
}
function levelOf(analyser, buf) {
  if (!analyser) return 0;
  analyser.getByteTimeDomainData(buf);
  let sum = 0;
  for (let i = 0; i < buf.length; i++) { const v = (buf[i] - 128) / 128; sum += v * v; }
  return Math.min(1, Math.sqrt(sum / buf.length) * 4);
}

// ── Lexa's orb: Nathan's idea, in her own colours ─────────────────────────
function Orb({ levelRef, modeRef }) {
  const ref = useRef(null);
  useEffect(() => {
    const cv = ref.current;
    if (!cv) return undefined;
    const ctx = cv.getContext('2d');
    const W = cv.width; const H = cv.height; const CX = W / 2; const CY = H / 2;
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const t0 = performance.now();
    let amp = 0; let raf = 0;
    function blob(r, wobble, t, phase, a, b, alpha) {
      const g = ctx.createRadialGradient(CX - r * 0.25, CY - r * 0.3, r * 0.1, CX, CY, r * 1.15);
      g.addColorStop(0, a); g.addColorStop(1, b);
      ctx.beginPath();
      for (let i = 0; i <= 120; i++) {
        const ang = (i / 120) * Math.PI * 2;
        const d = 1 + wobble * 0.55 * Math.sin(3 * ang + t * 1.7 + phase) + wobble * 0.35 * Math.sin(5 * ang - t * 2.3 + phase * 2) + wobble * 0.22 * Math.sin(7 * ang + t * 3.1 - phase);
        const x = CX + Math.cos(ang) * r * d; const y = CY + Math.sin(ang) * r * d;
        if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      }
      ctx.closePath();
      ctx.globalAlpha = alpha; ctx.fillStyle = g; ctx.fill(); ctx.globalAlpha = 1;
    }
    function frame(now) {
      const t = (now - t0) / 1000;
      const mode = modeRef.current;
      const target = mode === 'speaking' ? Math.max(0.15, levelRef.current) : 0;
      amp += (target - amp) * 0.22;
      const k = reduce ? 0.35 : 1;
      ctx.clearRect(0, 0, W, H);
      const breathe = mode === 'listening' ? Math.sin(t * 1.4) * 0.025 : 0;
      const base = W * (0.2 + breathe * 0.8 + amp * 0.04 * k);
      const wob = (mode === 'speaking' ? 0.025 + amp * 0.08 : mode === 'thinking' ? 0.03 : 0.018) * k;
      const glowR = Math.min(base * 1.9, W * 0.48);
      const glow = ctx.createRadialGradient(CX, CY, base * 0.6, CX, CY, glowR);
      glow.addColorStop(0, 'rgba(157,116,245,' + (0.24 + amp * 0.25) + ')');
      glow.addColorStop(1, 'rgba(157,116,245,0)');
      ctx.fillStyle = glow; ctx.beginPath(); ctx.arc(CX, CY, glowR, 0, Math.PI * 2); ctx.fill();
      ctx.globalCompositeOperation = 'lighter';
      blob(base * 1.04, wob * 1.2, t * 0.8, 0, 'rgba(110,72,220,0.9)', 'rgba(40,20,80,0)', 0.75);
      blob(base * 0.98, wob, t, 1.7, 'rgba(176,132,255,0.9)', 'rgba(60,30,110,0)', 0.8);
      blob(base * 0.86, wob * 0.8, t * 1.25, 3.1, 'rgba(255,140,190,0.55)', 'rgba(120,40,80,0)', 0.5);
      ctx.globalCompositeOperation = 'source-over';
      const hl = ctx.createRadialGradient(CX - base * 0.35, CY - base * 0.45, 0, CX - base * 0.35, CY - base * 0.45, base * 0.7);
      hl.addColorStop(0, 'rgba(255,255,255,0.28)'); hl.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = hl; ctx.beginPath(); ctx.arc(CX, CY, base * 0.98, 0, Math.PI * 2); ctx.fill();
      if (mode === 'thinking') {
        for (let i = 0; i < 14; i++) {
          const a = t * 1.6 + (i / 14) * Math.PI * 2;
          const r = base * 1.22 + Math.sin(t * 3 + i) * 6;
          ctx.globalAlpha = 0.25 + 0.6 * (0.5 + 0.5 * Math.sin(t * 4 - i * 0.6));
          ctx.fillStyle = '#D2BCFF';
          ctx.beginPath(); ctx.arc(CX + Math.cos(a) * r, CY + Math.sin(a) * r, 4, 0, Math.PI * 2); ctx.fill();
        }
        ctx.globalAlpha = 1;
      }
      raf = requestAnimationFrame(frame);
    }
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [levelRef, modeRef]);
  return <canvas ref={ref} className="gtir-orb" width="720" height="720" aria-hidden="true" />;
}

function MicMeter({ levelRef, muted }) {
  const ref = useRef(null);
  useEffect(() => {
    let raf = 0;
    function frame() {
      const el = ref.current;
      if (el) {
        const v = muted ? 0 : levelRef.current;
        Array.from(el.children).forEach((b, i) => {
          const h = 3 + Math.max(0, Math.min(1, v * (1.4 - Math.abs(i - 2) * 0.25))) * 13;
          b.style.height = h.toFixed(1) + 'px';
        });
      }
      raf = requestAnimationFrame(frame);
    }
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, [levelRef, muted]);
  return <div className="gtir-lvl" ref={ref} aria-hidden="true"><span /><span /><span /><span /><span /></div>;
}

function Brand({ title }) {
  return (
    <div className="gtir-brand">
      <img src="/favicon-512.png" alt="" className="gtir-mark" />
      <div><b>{title || 'Lexa'}</b><span>Generative Talent Co</span></div>
    </div>
  );
}
function Shell({ title, children }) {
  return (
    <div className="gtir gtir--lexa">
      <div className="gtir-wrap">
        <header className="gtir-top"><Brand title={title} /></header>
        {children}
      </div>
    </div>
  );
}
function Message({ title, heading, children, action }) {
  return (
    <Shell title={title}>
      <section className="gtir-card gtir-msg">
        <span className="gtir-orbmini gtlx-orbmini" aria-hidden="true" />
        <h1>{heading}</h1>
        {children}
        {action}
      </section>
    </Shell>
  );
}
function homeLink(info) { return info && info.is_test ? '/interviews/lexa' : '/?lexa=open'; }

export default function GtLexaRoom({ token }) {
  const [info, setInfo] = useState(null);
  const [stage, setStage] = useState('loading'); // loading notice check call done paused dropped closed failed gone error
  const [error, setError] = useState('');
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [deviceError, setDeviceError] = useState('');
  const [segments, setSegments] = useState([]);
  const [agentState, setAgentState] = useState('initializing');
  const [question, setQuestion] = useState(0);
  const [muted, setMuted] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [confirmEnd, setConfirmEnd] = useState(false);
  const [needsAudio, setNeedsAudio] = useState(false);
  const [lexaLate, setLexaLate] = useState(false);
  const [showTx, setShowTx] = useState(false);
  const [devicesReady, setDevicesReady] = useState(false);

  const roomRef = useRef(null);
  const tracksRef = useRef([]);
  const previewRef = useRef(null);
  const audioBoxRef = useRef(null);
  const txEndRef = useRef(null);
  const acRef = useRef(null);
  const micAnalyser = useRef(null);
  const lexaAnalyser = useRef(null);
  const mixRef = useRef(null);
  const micLevel = useRef(0);
  const lexaLevel = useRef(0);
  const modeRef = useRef('listening');
  const recRef = useRef(null);
  const uploadRef = useRef({ session: 0, seq: 0, queue: Promise.resolve(), mime: null });
  const leavingRef = useRef(null);
  const devGen = useRef(0);
  const [devices, setDevices] = useState({ audioinput: [], videoinput: [], audiooutput: [] });
  const [chosen, setChosen] = useState({ audioinput: '', videoinput: '', audiooutput: '' });
  const [tone, setTone] = useState(false);

  const load = useCallback(async () => {
    try { const v = await call(token); setInfo(v); return v; } catch (e) {
      if (e.status === 404) setStage('gone'); else { setError(e.message); setStage('error'); }
      return null;
    }
  }, [token]);

  useEffect(() => {
    let live = true;
    (async () => {
      const v = await load();
      if (!live || !v) return;
      if (v.status === 'completed') setStage('done');
      else if (v.status === 'failed') setStage('failed');
      else if (v.status === 'expired') setStage('closed');
      else setStage(v.notice_given ? 'check' : 'notice');
    })();
    return () => { live = false; };
  }, [load]);

  useEffect(() => {
    let raf = 0;
    const buf = new Uint8Array(1024);
    function frame() {
      micLevel.current = micLevel.current * 0.6 + levelOf(micAnalyser.current, buf) * 0.4;
      lexaLevel.current = lexaLevel.current * 0.5 + levelOf(lexaAnalyser.current, buf) * 0.5;
      raf = requestAnimationFrame(frame);
    }
    raf = requestAnimationFrame(frame);
    return () => cancelAnimationFrame(raf);
  }, []);

  useEffect(() => {
    modeRef.current = agentState === 'speaking' ? 'speaking' : agentState === 'thinking' ? 'thinking' : 'listening';
  }, [agentState]);

  useEffect(() => {
    if (stage !== 'call') return undefined;
    const t = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [stage]);

  useEffect(() => { if (txEndRef.current) txEndRef.current.scrollIntoView({ block: 'end' }); }, [segments]);

  useEffect(() => {
    if (stage !== 'call') return undefined;
    const h = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, [stage]);

  const audioContext = useCallback(() => {
    if (!acRef.current) {
      const AC = window.AudioContext || window.webkitAudioContext;
      acRef.current = AC ? new AC() : null;
    }
    if (acRef.current && acRef.current.state === 'suspended') acRef.current.resume().catch(() => {});
    return acRef.current;
  }, []);

  const stopTracks = useCallback(() => {
    tracksRef.current.forEach((t) => { try { t.stop(); } catch (e) { /* gone */ } });
    tracksRef.current = [];
    micAnalyser.current = null;
    setDevicesReady(false);
  }, []);

  // ── Camera and microphone check ─────────────────────────────────────────
  const startDevices = useCallback(async (pick) => {
    const gen = ++devGen.current;
    const want = pick || {};
    setDeviceError('');
    stopTracks();
    try {
      const tracks = await createLocalTracks({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, ...(want.audioinput ? { deviceId: want.audioinput } : {}) },
        video: { resolution: VideoPresets.h360.resolution, ...(want.videoinput ? { deviceId: want.videoinput } : { facingMode: 'user' }) },
      });
      if (gen !== devGen.current) { tracks.forEach((t) => { try { t.stop(); } catch (e) { /* gone */ } }); return; }
      tracksRef.current = tracks;
      try {
        const all = await navigator.mediaDevices.enumerateDevices();
        const list = (k) => all.filter((d) => d.kind === k && d.deviceId).map((d, i) => ({ id: d.deviceId, label: d.label || (k === 'audioinput' ? 'Microphone ' : k === 'videoinput' ? 'Camera ' : 'Speaker ') + (i + 1) }));
        setDevices({ audioinput: list('audioinput'), videoinput: list('videoinput'), audiooutput: list('audiooutput') });
        const id = (k) => { const t = tracks.find((x) => x.kind === (k === 'audioinput' ? Track.Kind.Audio : Track.Kind.Video)); const st = t && t.mediaStreamTrack.getSettings ? t.mediaStreamTrack.getSettings() : {}; return st.deviceId || ''; };
        setChosen((c) => ({ ...c, audioinput: id('audioinput'), videoinput: id('videoinput') }));
      } catch (e) { /* lists stay empty */ }
      setDevicesReady(true);
      const v = tracks.find((t) => t.kind === Track.Kind.Video);
      if (v && previewRef.current) v.attach(previewRef.current);
      const a = tracks.find((t) => t.kind === Track.Kind.Audio);
      const ac = audioContext();
      if (a && ac) {
        const an = ac.createAnalyser(); an.fftSize = 1024;
        ac.createMediaStreamSource(new MediaStream([a.mediaStreamTrack])).connect(an);
        micAnalyser.current = an;
      }
    } catch (e) {
      const name = (e && e.name) || '';
      if (gen !== devGen.current) return;
      setDeviceError(name === 'NotAllowedError' || name === 'PermissionDeniedError'
        ? 'Your browser blocked the camera or microphone. Allow both for this page (look for the camera icon in the address bar), then try again.'
        : name === 'NotFoundError' || name === 'DevicesNotFoundError'
          ? 'We could not find a camera or microphone. Plug one in, or switch to a device that has both, then try again.'
          : name === 'NotReadableError'
            ? 'Another app is using your camera or microphone. Close it (for example a video call), then try again.'
            : 'We could not start your camera and microphone. Try again, or use a different browser.');
    }
  }, [audioContext, stopTracks]);

  useEffect(() => { if (stage === 'check') startDevices(); }, [stage, startDevices]);

  function pickDevice(kind, id) {
    const next = { ...chosen, [kind]: id };
    setChosen(next);
    if (kind !== 'audiooutput') startDevices(next);
  }

  async function testSpeakers() {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (!AC || tone) return;
    setTone(true);
    try {
      const ac = new AC();
      const dest = ac.createMediaStreamDestination();
      const el = new Audio();
      el.srcObject = dest.stream;
      if (chosen.audiooutput && el.setSinkId) { try { await el.setSinkId(chosen.audiooutput); } catch (e) { /* default speaker */ } }
      await el.play().catch(() => {});
      [[660, 0], [880, 0.35]].forEach(([f, at]) => {
        const o = ac.createOscillator(); const g = ac.createGain();
        o.type = 'sine'; o.frequency.value = f;
        g.gain.setValueAtTime(0, ac.currentTime + at);
        g.gain.linearRampToValueAtTime(0.35, ac.currentTime + at + 0.03);
        g.gain.exponentialRampToValueAtTime(0.001, ac.currentTime + at + 0.6);
        o.connect(g); g.connect(dest);
        o.start(ac.currentTime + at); o.stop(ac.currentTime + at + 0.65);
      });
      setTimeout(() => { try { el.pause(); ac.close(); } catch (e) { /* closed */ } setTone(false); }, 1300);
    } catch (e) { setTone(false); }
  }

  useEffect(() => () => {
    try { if (roomRef.current) roomRef.current.disconnect(); } catch (e) { /* closing */ }
    stopTracks();
  }, [stopTracks]);

  async function giveNotice() {
    setBusy(true); setError('');
    try { setInfo(await call(token, '/notice', { agree: true })); setStage('check'); } catch (e) { setError(e.message); } finally { setBusy(false); }
  }

  // ── Recording ──────────────────────────────────────────────────────────
  function upload(blob) {
    const u = uploadRef.current;
    const seq = u.seq; u.seq += 1;
    const session = u.session; const mime = u.mime;
    u.queue = u.queue.then(async () => {
      for (let attempt = 0; attempt < 4; attempt++) {
        try {
          const res = await fetch(API_BASE + '/api/public/lexa/' + token + '/recording?session=' + session + '&seq=' + seq + '&mime=' + encodeURIComponent(mime || ''), {
            method: 'POST', headers: { 'Content-Type': 'application/octet-stream' }, body: blob,
          });
          if (res.ok || res.status === 409 || res.status === 413) return;
        } catch (e) { /* retry */ }
        await new Promise((r) => setTimeout(r, 1500 * (attempt + 1)));
      }
    });
  }
  function prepareMix() {
    const ac = audioContext();
    mixRef.current = null;
    if (!ac) return;
    const dest = ac.createMediaStreamDestination();
    const a = tracksRef.current.find((t) => t.kind === Track.Kind.Audio);
    if (a) ac.createMediaStreamSource(new MediaStream([a.mediaStreamTrack])).connect(dest);
    mixRef.current = dest;
  }
  function startRecording(session) {
    const mime = pickMime();
    const dest = mixRef.current;
    const v = tracksRef.current.find((t) => t.kind === Track.Kind.Video);
    if (!mime || !dest || !v) return;
    uploadRef.current = { session, seq: 0, queue: Promise.resolve(), mime };
    try {
      const rec = new MediaRecorder(new MediaStream([v.mediaStreamTrack].concat(dest.stream.getAudioTracks())),
        { mimeType: mime, videoBitsPerSecond: 280000, audioBitsPerSecond: 48000 });
      rec.ondataavailable = (e) => { if (e.data && e.data.size) upload(e.data); };
      rec.start(PIECE_MS);
      recRef.current = rec;
    } catch (e) { recRef.current = null; }
  }
  async function stopRecording() {
    const rec = recRef.current;
    recRef.current = null;
    if (rec && rec.state !== 'inactive') {
      await new Promise((resolve) => {
        rec.addEventListener('stop', resolve, { once: true });
        try { rec.stop(); } catch (e) { resolve(); }
        setTimeout(resolve, 3000);
      });
    }
    await Promise.race([uploadRef.current.queue, new Promise((r) => setTimeout(r, 20000))]);
  }

  // ── The call ───────────────────────────────────────────────────────────
  function addSegment(id, who, text, final) {
    setSegments((prev) => {
      const at = prev.findIndex((x) => x.id === id);
      if (at === -1) return prev.concat([{ id, who, text, final }]);
      const next = prev.slice();
      next[at] = { ...next[at], text, final: final || next[at].final };
      return next;
    });
  }
  function readAgent(p) {
    const at = p.attributes || {};
    if (at['lk.agent.state']) setAgentState(at['lk.agent.state']);
    if (at['gt.question']) setQuestion(Number(at['gt.question']) || 0);
  }

  async function join() {
    setBusy(true); setError(''); setLexaLate(false);
    audioContext();
    try {
      const j = await call(token, '/join', { name: info && info.first_name });
      prepareMix();
      const room = new Room({ adaptiveStream: true, dynacast: true, ...(chosen.audiooutput ? { audioOutput: { deviceId: chosen.audiooutput } } : {}) });
      roomRef.current = room;
      leavingRef.current = null;

      room.on(RoomEvent.TrackSubscribed, (track) => {
        if (track.kind !== Track.Kind.Audio) return;
        const el = track.attach();
        if (audioBoxRef.current) audioBoxRef.current.appendChild(el);
        const ac = audioContext();
        if (ac) {
          const src = ac.createMediaStreamSource(new MediaStream([track.mediaStreamTrack]));
          const an = ac.createAnalyser(); an.fftSize = 1024;
          src.connect(an);
          lexaAnalyser.current = an;
          if (mixRef.current) src.connect(mixRef.current);
        }
      });
      room.on(RoomEvent.TrackUnsubscribed, (track) => { track.detach().forEach((el) => el.remove()); });
      room.on(RoomEvent.ParticipantConnected, (p) => { setLexaLate(false); readAgent(p); });
      room.on(RoomEvent.ParticipantAttributesChanged, (changed, p) => { if (p && p !== room.localParticipant) readAgent(p); });
      room.on(RoomEvent.AudioPlaybackStatusChanged, () => setNeedsAudio(!room.canPlaybackAudio));
      room.registerTextStreamHandler(TOPIC, async (reader, from) => {
        const attrs = (reader.info && reader.info.attributes) || {};
        const id = attrs['lk.segment_id'] || (reader.info && reader.info.id);
        const who = String((from && from.identity) || '').indexOf('cand-') === 0 ? 'you' : 'lexa';
        let text = '';
        try {
          for await (const chunk of reader) { text += chunk; addSegment(id, who, text, false); }
        } catch (e) { /* stream cut short */ }
        addSegment(id, who, text, attrs['lk.transcription_final'] === 'true' || who === 'lexa');
      });
      room.on(RoomEvent.Disconnected, async () => {
        lexaAnalyser.current = null;
        await stopRecording();
        stopTracks();
        const why = leavingRef.current;
        const v = await load();
        if (v && v.status === 'completed') setStage('done');
        else if (v && v.status === 'failed') setStage('failed');
        else if (why === 'paused') setStage('paused');
        else setStage('dropped');
      });

      await room.connect(j.url, j.token);
      for (const t of tracksRef.current) {
        await room.localParticipant.publishTrack(t, t.kind === Track.Kind.Audio ? { dtx: false } : { simulcast: false });
      }
      try { await room.startAudio(); } catch (e) { /* asked for below */ }
      setNeedsAudio(!room.canPlaybackAudio);
      room.remoteParticipants.forEach(readAgent);
      setSegments([]); setElapsed((info && info.used_seconds) || 0); setMuted(false);
      startRecording(j.session);
      setStage('call');
      setTimeout(() => { if (roomRef.current === room && room.remoteParticipants.size === 0) setLexaLate(true); }, 25000);
    } catch (e) {
      setError(e.code === 'no_credit' ? 'You have no sessions left. Buy one in the Lexa tab of your portal, then come back to this link.' : (e.message || 'We could not start the call.'));
      try { if (roomRef.current) await roomRef.current.disconnect(); } catch (e2) { /* never connected */ }
    } finally { setBusy(false); }
  }

  async function toggleMute() {
    const a = tracksRef.current.find((t) => t.kind === Track.Kind.Audio);
    if (!a) return;
    if (muted) await a.unmute(); else await a.mute();
    setMuted(!muted);
  }
  async function repeat() {
    try { await roomRef.current.localParticipant.sendText('Could you say that again, please?', { topic: 'lk.chat' }); } catch (e) { /* not connected */ }
  }
  async function leave(reason) {
    setConfirmEnd(false);
    leavingRef.current = reason;
    try { await call(token, '/leave', { reason }); } catch (e) { /* the disconnect still happens */ }
    try { await roomRef.current.disconnect(); } catch (e) { /* already gone */ }
  }

  const title = info ? info.title : 'Lexa';
  const isInterview = info && info.kind === 'interview';

  if (stage === 'loading') return <Shell><div className="gtir-card gtir-loading">Loading your session…</div></Shell>;
  if (stage === 'gone') {
    return <Message heading="This link is not valid"><p>Check you opened the whole link. You can start a session from the Lexa tab in your portal.</p></Message>;
  }
  if (stage === 'error') {
    return <Message heading="Something went wrong" action={<button type="button" className="gtir-btn gtir-btn--go" onClick={() => window.location.reload()}>Try again</button>}><p>{error}</p></Message>;
  }
  if (stage === 'closed') {
    return <Message title={title} heading="This session has closed"><p>It was set up more than a week ago and never started, so no credit was used. Start a new one from the Lexa tab in your portal.</p></Message>;
  }
  if (stage === 'failed') {
    return (
      <Message title={title} heading="The call did not work this time"
        action={<a className="gtir-btn gtir-btn--go" href={homeLink(info)}>Back to Lexa</a>}>
        <p>{info && info.is_test ? 'Something went wrong on our side. Nothing is recorded against a test run.' : 'Something went wrong on our side, so your credit has been given back. Start a new session whenever you are ready.'}</p>
      </Message>
    );
  }
  if (stage === 'done') {
    return (
      <Message title={title} heading="Thank you, that is the session done"
        action={<a className="gtir-btn gtir-btn--go" href={homeLink(info)}>{info && info.is_test ? 'Back to the Lexa screen' : 'Go to your Lexa page'}</a>}>
        <p>Lexa is writing your report now. It will be on your Lexa page in a minute or two, with the recording and the full transcript, so you can come back to it whenever you like.</p>
      </Message>
    );
  }
  if (stage === 'paused' || stage === 'dropped') {
    return (
      <Message title={title} heading={stage === 'paused' ? 'Your session is paused' : 'The call dropped'}
        action={<button type="button" className="gtir-btn gtir-btn--go" onClick={() => setStage('check')}>{stage === 'paused' ? 'Carry on now' : 'Rejoin the session'}</button>}>
        <p>Nothing is lost and no extra credit is used. Come back to this link and Lexa will pick up where you left off.</p>
      </Message>
    );
  }

  if (stage === 'notice') {
    return (
      <Shell title={title}>
        <section className="gtir-card gtir-notice">
          <div className="gtir-hello">
            <span className="gtir-orbmini gtlx-orbmini" aria-hidden="true" />
            <div>
              <p className="gtir-eyebrow">{info.is_test ? 'Test run' : isInterview ? (info.mode === 'real' ? 'Mock interview, Real mode' : 'Mock interview, Practice mode') : 'Career coaching'}</p>
              <h1>{info.first_name ? 'Hello ' + info.first_name + ', meet Lexa' : 'Meet Lexa'}</h1>
            </div>
          </div>
          <p className="gtir-lede">
            Lexa is Generative Talent Co's AI coach. You will talk for about {info.minutes} minutes
            {isInterview ? <> about <b>{info.role_title || 'the role you chose'}</b>.</> : <> about <b>{(info.topic || 'your career').toLowerCase()}</b>.</>}
          </p>
          <ul className="gtir-points">
            <li><b>Lexa is artificial intelligence, not a person.</b> Her feedback is coaching, not an assessment, and it never affects whether we put you forward for a role.</li>
            <li><b>The session is recorded and transcribed.</b> Your camera and both voices are recorded. The recording, transcript and a written report are saved on your profile for you to watch again, and the Generative Talent Co team can see them. They are never shared with employers.</li>
            <li><b>Talk as you would to a coach.</b> Take your time, think out loud, ask her to say something again. {isInterview && info.mode === 'practice' ? 'After each answer she will coach you and offer a retry.' : isInterview ? 'She will interview you straight through, then give you a debrief at the end.' : 'She will finish by agreeing next steps with you.'}</li>
            {!info.is_test && !info.charged && <li><b>One credit is used when the call starts.</b> If the call fails on our side, it is given back automatically.</li>}
            <li><b>If the call drops,</b> open this link again to carry on.</li>
          </ul>
          <p className="gtir-fine">GTCO Ltd is responsible for your data. You can download it or ask us to delete it at any time from your portal.</p>
          <label className="gtir-agree">
            <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} />
            <span>I understand Lexa is artificial intelligence, and I agree to the session being recorded, transcribed and saved on my profile.</span>
          </label>
          {error && <p className="gtir-err" role="alert">{error}</p>}
          <div className="gtir-actions">
            <button type="button" className="gtir-btn gtir-btn--go" disabled={!agree || busy} onClick={giveNotice}>{busy ? 'One moment…' : 'Continue'}</button>
          </div>
        </section>
      </Shell>
    );
  }

  if (stage === 'check') {
    return (
      <Shell title={title}>
        <section className="gtir-card gtir-check">
          <div className="gtir-check-v">
            <video ref={previewRef} className="gtir-preview" autoPlay muted playsInline />
            {!devicesReady && !deviceError && <span className="gtir-preview-wait">Starting your camera…</span>}
          </div>
          <div className="gtir-check-t">
            <p className="gtir-eyebrow">Before you start</p>
            <h1>Check you can be seen and heard</h1>
            <div className="gtir-dev">
              <div className="gtir-dev-row">
                <span className="gtir-dev-k">Camera</span>
                <select className="gtir-sel" aria-label="Camera" value={chosen.videoinput} onChange={(e) => pickDevice('videoinput', e.target.value)} disabled={!devices.videoinput.length}>
                  {devices.videoinput.length ? devices.videoinput.map((d) => <option key={d.id} value={d.id}>{d.label}</option>) : <option value="">Default camera</option>}
                </select>
              </div>
              <div className="gtir-dev-row">
                <span className="gtir-dev-k">Microphone</span>
                <select className="gtir-sel" aria-label="Microphone" value={chosen.audioinput} onChange={(e) => pickDevice('audioinput', e.target.value)} disabled={!devices.audioinput.length}>
                  {devices.audioinput.length ? devices.audioinput.map((d) => <option key={d.id} value={d.id}>{d.label}</option>) : <option value="">Default microphone</option>}
                </select>
                <div className="gtir-dev-meter"><MicMeter levelRef={micLevel} muted={false} /><span>Say something: the bars should move</span></div>
              </div>
              <div className="gtir-dev-row">
                <span className="gtir-dev-k">Speaker</span>
                {devices.audiooutput.length > 0 && (
                  <select className="gtir-sel" aria-label="Speaker" value={chosen.audiooutput} onChange={(e) => pickDevice('audiooutput', e.target.value)}>
                    <option value="">System default</option>
                    {devices.audiooutput.map((d) => <option key={d.id} value={d.id}>{d.label}</option>)}
                  </select>
                )}
                <button type="button" className="gtir-btn gtir-btn--sm" onClick={testSpeakers} disabled={tone}>{tone ? 'Playing…' : 'Play a test sound'}</button>
              </div>
            </div>
            <p className="gtir-muted">Somewhere quiet, face lit from the front. Headphones help.</p>
            {deviceError && <p className="gtir-err" role="alert">{deviceError} <button type="button" className="gtir-link" onClick={() => startDevices(chosen)}>Try again</button></p>}
            {!info.live && <p className="gtir-err" role="alert">Lexa is not switched on yet. Please try again later.</p>}
            {error && <p className="gtir-err" role="alert">{error}</p>}
            <div className="gtir-actions">
              <button type="button" className="gtir-btn gtir-btn--go" disabled={busy || !!deviceError || !info.live || !devicesReady} onClick={join}>
                {busy ? 'Connecting to Lexa…' : info.started ? 'Carry on with Lexa' : 'Start the session'}
              </button>
            </div>
            <p className="gtir-muted">About {info.minutes} minutes. You can pause and come back to this link.</p>
          </div>
        </section>
      </Shell>
    );
  }

  // stage === 'call'
  const latest = segments.length ? segments[segments.length - 1] : null;
  const stateText = agentState === 'speaking' ? 'Speaking' : agentState === 'thinking' ? 'Thinking' : agentState === 'initializing' ? 'Joining' : 'Listening';
  const left = Math.max(0, (info.minutes || 20) * 60 - elapsed);
  return (
    <div className="gtir gtir--call gtir--lexa">
      <div className="gtir-wrap gtir-wrap--wide">
        <header className="gtir-top">
          <Brand title={title} />
          <div className="gtir-meta">
            <span className="gtir-chip"><i className="gtir-rec" aria-hidden="true" />Recording</span>
            {isInterview && question > 0 && <span className="gtir-chip">Question <b>{question}</b></span>}
            <span className="gtir-chip" title="About 20 minutes in all">{left > 0 ? <><b>{clock(left)}</b> left</> : <b>Wrapping up</b>}</span>
            <button type="button" className="gtir-chip gtir-chip--btn gtir-only-narrow" onClick={() => setShowTx(!showTx)}>{showTx ? 'Hide transcript' : 'Transcript'}</button>
          </div>
        </header>

        <div className={'gtir-main' + (showTx ? ' gtir-main--tx' : '')}>
          <section className="gtir-stage" aria-label="Coaching call">
            <div className="gtir-orbwrap">
              <Orb levelRef={lexaLevel} modeRef={modeRef} />
              <div className="gtir-who">
                <b>Lexa</b>
                <span>AI coach</span>
                <div><span className={'gtir-state gtir-state--' + agentState}><i />{stateText}</span></div>
              </div>
            </div>
            <div className="gtir-captions" aria-live="polite">
              {lexaLate ? (
                <p className="gtir-cap-note">Lexa is taking longer than usual to join. Wait a moment, or pause and try again in a few minutes. If she does not join, your credit is given back.</p>
              ) : latest ? (
                <>
                  <span className={'gtir-speaker' + (latest.who === 'you' ? ' gtir-speaker--me' : '')}>{latest.who === 'you' ? 'You' : 'Lexa'}</span>
                  <p>{latest.text}</p>
                </>
              ) : (
                <p className="gtir-cap-note">Lexa is joining. She will say hello in a moment.</p>
              )}
            </div>
            <div className="gtir-self" aria-label="Your camera">
              <video ref={(el) => { const v = tracksRef.current.find((t) => t.kind === Track.Kind.Video); if (el && v && !el.srcObject) v.attach(el); }} autoPlay muted playsInline />
              <span className="gtir-self-lab">You{muted ? ' (muted)' : ''}</span>
              <MicMeter levelRef={micLevel} muted={muted} />
            </div>
            {needsAudio && (
              <button type="button" className="gtir-sound" onClick={() => roomRef.current && roomRef.current.startAudio().then(() => setNeedsAudio(false)).catch(() => {})}>Tap to hear Lexa</button>
            )}
            <div className="gtir-bar" role="toolbar" aria-label="Call controls">
              <button type="button" onClick={toggleMute} aria-pressed={muted}>{muted ? 'Unmute' : 'Mute'}</button>
              <button type="button" onClick={repeat}>Say that again</button>
              <button type="button" className="gtir-end" onClick={() => setConfirmEnd(true)}>End session</button>
            </div>
          </section>

          <aside className="gtir-tx" aria-label="Transcript">
            <header><b>Transcript</b><span className="gtir-live"><i />Live</span></header>
            <div className="gtir-txlist">
              {segments.length === 0 && <p className="gtir-muted">What you and Lexa say appears here as you talk.</p>}
              {segments.map((s) => (
                <div key={s.id} className={'gtir-turn gtir-turn--' + (s.who === 'you' ? 'you' : 'lexa') + (s.final ? '' : ' gtir-turn--live')}>
                  <b>{s.who === 'you' ? 'You' : 'Lexa'}</b>
                  <p>{s.text}</p>
                </div>
              ))}
              <div ref={txEndRef} />
            </div>
          </aside>
        </div>
      </div>
      <div ref={audioBoxRef} hidden />

      {confirmEnd && (
        <div className="gtir-modal-bg" onClick={() => setConfirmEnd(false)}>
          <div className="gtir-modal" role="dialog" aria-modal="true" aria-label="End the session" onClick={(e) => e.stopPropagation()}>
            <h2>End the session?</h2>
            <p>If you pause, you can carry on from this link later. If you end it, Lexa writes your report from what you have covered so far.</p>
            <div className="gtir-modal-acts">
              <button type="button" className="gtir-btn" onClick={() => setConfirmEnd(false)}>Keep going</button>
              <button type="button" className="gtir-btn" onClick={() => leave('paused')}>Pause and come back</button>
              <button type="button" className="gtir-btn gtir-btn--end" onClick={() => leave('ended')}>End the session</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
